// The trending-searches wire.
//
// ORACLES (all three identical — the endpoint is traced, not invented):
//   1. Sanctioned client @eventbrite/consumer-search.search-bar
//      dist/esm/api/trending.{js,d.ts}:
//        sdkRequest(`/api/v3/trending/?location=${slug}&timeframe=week`)
//        -> `TrendingSearchesResponse` (reproduced verbatim below).
//   2. eb-ui packages/attendee/search-takeover/src/api/trending.ts  (same URL).
//   3. eb-ui packages/attendee/discover-lib/src/api/trendingSearches.ts:
//        getTrendingSearches(location, timeframe = 'month')  (same URL, timeframe param).
//
// Why we reproduce the wire instead of importing the sanctioned client:
//   - it hardcodes `timeframe=week` (harness directive: parameterize it);
//   - it fetches a RELATIVE url via @eventbrite/legacy-infra.http `sdkRequest`, which
//     has no origin inside a Lambda getServerSideProps;
//   - it drags a heavy react-18-era UI graph (chatbot / redux / location-autocomplete /
//     react-use) inappropriate for this lean SEO page.
// We keep its exact contract (URL + response type) and fetch it correctly per context.

import type { IncomingHttpHeaders } from 'http';
import { getCoreApiSubdomain, getEbDomain } from './coreHost';

export interface TrendingSearch {
    /** Display value, e.g. `christmas party`. */
    term: string;
    /** Term ranking. */
    score: number;
    /** URL-ready search term, e.g. `christmas-party`. */
    formatted: string;
}

export interface TrendingSearchesResponse {
    trending: {
        active_timeframe: string;
        trending_searches: TrendingSearch[];
    };
}

const TRENDING_SEARCH_PATH = '/api/v3/trending/';

function buildRelativeUrl(location: string, timeframe: string): string {
    const params = new URLSearchParams({ location, timeframe });
    return `${TRENDING_SEARCH_PATH}?${params.toString()}`;
}

/**
 * SERVER-side fetch (getServerSideProps). Hits the absolute internal-api host —
 * same approach as the sibling homepage-webapp server Core fetch
 * (`src/api/search/searchApiV3.ts`): `internal-api.<domain>`, forwarding the
 * viewer's cookie. A GET needs no CSRF (Django double-submit guards unsafe methods only).
 */
export async function fetchTrendingSearchesServer(
    location: string,
    timeframe: string,
    req: { headers: IncomingHttpHeaders },
): Promise<TrendingSearchesResponse> {
    const ebDomain = getEbDomain(req);
    const subdomain = getCoreApiSubdomain();
    const url = `https://${subdomain}.${ebDomain}${buildRelativeUrl(location, timeframe)}`;
    const cookie = (req.headers['cookie'] as string) || '';

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Referer: `https://${subdomain}.${ebDomain}`,
            ...(cookie ? { cookie } : {}),
        },
    });

    if (!response.ok) {
        throw new Error(
            `[trending] ${TRENDING_SEARCH_PATH} responded ${response.status} for location=${location} timeframe=${timeframe}`,
        );
    }

    return (await response.json()) as TrendingSearchesResponse;
}

// The app's own BFF route (this Next app's `<basePath>/api/trending/`). Keep in sync with
// next.config.js `basePath`; the trailing slash is required (next.config `trailingSlash:
// true` 308-redirects the un-slashed form). The client calls THIS — never Core directly
// (Valkyrie: the webapp owns its data pipeline; see src/pages/api/trending.page.ts).
const TRENDING_BFF_PATH = '/trending/searches/api/trending/';

/**
 * CLIENT-side re-fetch (timeframe switch). Calls the app's OWN BFF route, same-origin,
 * served by this Next app — NOT Core. This is the Valkyrie contract (client→BFF; the
 * client never talks to Core), and because it's the app's own origin it also works on
 * localhost (no missing-Core-origin dead-tab problem the direct relative Core fetch had).
 * A plain same-origin GET suffices here (public data, no auth); use
 * `@eventbrite/webapp-api-client` instead when a BFF call needs eb-* auth headers/CSRF.
 */
export async function fetchTrendingSearchesClient(
    location: string,
    timeframe: string,
): Promise<TrendingSearchesResponse> {
    const params = new URLSearchParams({ location, timeframe });
    const response = await fetch(`${TRENDING_BFF_PATH}?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
    });

    if (!response.ok) {
        throw new Error(`[trending] BFF re-fetch responded ${response.status}`);
    }

    return (await response.json()) as TrendingSearchesResponse;
}
